<template>
  <div>
    <div
      v-if="otpFlow === null"
      class="p-6 lg:px-8 lg:pt-4 lg:pb-8"
    >
      <p class="text-center text-black font-medium mb-6">
        Por ser tu primera vez, para crear tu suscripción necesitarás completar
        los siguientes datos.
      </p>

      <form @submit.prevent="handleSubmit" class="space-y-3">
        <div>
          <label class="form-label">Nombre</label>
          <input
            v-model="person.firstName"
            type="text"
            required
            :disabled="formDisabled"
            class="form-input lg:text-base"
            @focus="searching = false"
          />
        </div>

        <div>
          <label class="form-label">Apellido</label>
          <input
            v-model="person.lastName"
            type="text"
            required
            :disabled="formDisabled"
            class="form-input lg:text-base"
            @focus="searching = false"
          />
        </div>

        <div>
          <label class="form-label">Tipo de Documento</label>
          <Listbox v-model="selectedDocType" :disabled="formDisabled">
            <div class="relative">
              <ListboxButton
                class="form-listbox-btn lg:text-base"
              >
                <span class="block truncate">{{ selectedDocType }}</span>
                <span
                  class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                >
                  <SelectorIcon class="w-5 h-5 text-dark-gray" aria-hidden="true" />
                </span>
              </ListboxButton>

              <transition
                leave-active-class="transition duration-100 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <ListboxOptions
                  class="absolute w-full py-1 mt-1 overflow-auto bg-white z-10 rounded-lg shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-sm"
                >
                  <ListboxOption
                    v-slot="{ active, selected }"
                    v-for="docType in docList"
                    :key="docType"
                    :value="docType"
                    as="template"
                  >
                    <li
                      :class="[
                        active ? 'text-white bg-aces' : 'text-black',
                        'cursor-default select-none relative py-2 pl-10 pr-4',
                      ]"
                    >
                      <span
                        :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']"
                      >{{ docType }}</span>
                      <span
                        v-if="selected"
                        class="absolute inset-y-0 left-0 flex items-center pl-3"
                        :class="active ? 'text-white' : 'text-aces'"
                      >
                        <CheckIcon class="w-5 h-5" aria-hidden="true" />
                      </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>
        </div>

        <div>
          <label class="form-label">Número de Documento</label>
          <input
            v-model="person.documentNumber"
            type="number"
            required
            :disabled="formDisabled"
            class="form-input lg:text-base"
            @focus="searching = false"
            @input="validateData"
          />
        </div>

        <div>
          <label class="form-label">
            Email <span class="text-red-warning">*</span>
          </label>
          <input
            v-model="person.email"
            type="email"
            autocomplete="email"
            required
            :disabled="formDisabled"
            class="form-input lg:text-base"
            @focus="searching = false"
          />
        </div>

        <div>
          <label class="form-label">
            Celular <span class="text-red-warning">*</span>
            <span class="text-xs text-dark-gray ml-1">(diez dígitos, sin cero ni quince)</span>
          </label>
          <input
            v-model="person.phoneNumber"
            type="tel"
            inputmode="numeric"
            maxlength="10"
            required
            :disabled="formDisabled"
            :class="[
              'form-input lg:text-base',
              phoneError ? 'border-red-warning' : '',
            ]"
            @focus="searching = false"
            @input="onPhoneInput"
          />
          <p v-if="phoneError" class="text-xs text-red-warning mt-1">
            {{ phoneError }}
          </p>
        </div>

        <div>
          <label class="form-label">Provincia</label>
          <Listbox v-model="selectedProvince" :disabled="formDisabled">
            <div class="relative">
              <ListboxButton
                @focus="(searching = false), filterChurchList('')"
                class="form-listbox-btn lg:text-base"
              >
                <span class="block truncate">{{ selectedProvince?.provinceName }}</span>
                <span
                  class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                >
                  <SelectorIcon class="w-5 h-5 text-dark-gray" aria-hidden="true" />
                </span>
              </ListboxButton>

              <transition
                leave-active-class="transition duration-100 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <ListboxOptions
                  class="absolute w-full py-1 mt-1 overflow-auto bg-white z-10 rounded-lg shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-sm"
                >
                  <ListboxOption
                    v-slot="{ active, selected }"
                    v-for="province in provinces"
                    :key="province"
                    :value="province"
                    as="template"
                  >
                    <li
                      :class="[
                        active ? 'text-white bg-aces' : 'text-black',
                        'cursor-default select-none relative py-2 pl-10 pr-4',
                      ]"
                    >
                      <span
                        :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']"
                      >{{ province?.provinceName }}</span>
                      <span
                        v-if="selected"
                        class="absolute inset-y-0 left-0 flex items-center pl-3"
                        :class="active ? 'text-white' : 'text-aces'"
                      >
                        <CheckIcon class="w-5 h-5" aria-hidden="true" />
                      </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>
        </div>

        <div>
          <Listbox v-model="selectedChurch" :disabled="formDisabled">
            <label class="form-label">
              Iglesia
              <span class="text-xs italic">*Asegurate de que tu iglesia esté seleccionada.</span>
            </label>
            <div class="relative">
              <input
                id="searchBox"
                v-model="churchSearch"
                type="text"
                required
                autocomplete="off"
                :disabled="formDisabled"
                class="form-input lg:text-base"
                @input="filterChurchList(churchSearch)"
              />
              <div v-show="searching && filteredList.length > 0">
                <transition
                  leave-active-class="transition duration-100 ease-in"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                >
                  <ListboxOptions
                    static
                    class="absolute w-full py-1 mt-1 overflow-auto text-sm bg-white z-20 rounded-lg shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <ListboxOption
                      v-slot="{ active, selected }"
                      v-for="church in filteredList"
                      :key="church"
                      :value="church"
                      as="template"
                    >
                      <li
                        :class="[
                          active ? 'text-white bg-aces' : 'text-black',
                          'cursor-default select-none relative py-2 pl-12 pr-4',
                        ]"
                        @click="selectChurchOnClick(church)"
                      >
                        <span
                          :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']"
                        >{{ church.churchName }}</span>
                        <span
                          v-if="selected"
                          class="absolute inset-y-0 left-0 flex items-center pl-3"
                          :class="active ? 'text-white' : 'text-aces'"
                        >
                          <CheckIcon class="w-5 h-5" aria-hidden="true" />
                        </span>
                      </li>
                    </ListboxOption>
                  </ListboxOptions>
                </transition>
              </div>
            </div>
          </Listbox>
        </div>

        <div>
          <label class="form-label">Coordinador</label>
          <input
            :placeholder="selectedChurch?.coordinatorName"
            readonly
            class="form-input-readonly"
          />
        </div>

        <div class="banner-warning mt-6">
          <div class="flex items-start gap-3">
            <ExclamationCircleIcon class="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
            <div class="text-sm">
              <p class="font-medium text-warning-fg">Verificación requerida</p>
              <p class="text-warning-fg mt-1">
                Al continuar, te enviaremos un código por SMS y otro por email para
                verificar tus datos de contacto.
              </p>
            </div>
          </div>
        </div>

        <button
          type="submit"
          :disabled="submitDisabled"
          class="btn-primary mt-4 lg:text-lg disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {{ submitButtonLabel }}
        </button>
      </form>
    </div>

    <div
      v-else-if="otpFlow === 'verify-phone' || otpFlow === 'verify-email'"
      class="p-6 lg:px-8 lg:pt-4 lg:pb-8"
    >
      <div class="flex items-center justify-center gap-2 mb-6">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 rounded-full bg-aces flex items-center justify-center">
            <CheckIcon
              v-if="otpFlow === 'verify-email'"
              class="w-5 h-5 text-white"
            />
            <DeviceMobileIcon v-else class="w-4 h-4 text-white" />
          </div>
          <span
            :class="[
              otpFlow === 'verify-phone' ? 'text-black font-medium' : 'text-aces font-medium',
              'text-sm',
            ]"
          >Teléfono</span>
        </div>
        <div
          class="w-8 h-0.5"
          :class="otpFlow === 'verify-email' ? 'bg-aces' : 'bg-light-gray'"
        />
        <div class="flex items-center gap-2">
          <div
            class="w-8 h-8 rounded-full flex items-center justify-center"
            :class="otpFlow === 'verify-email' ? 'bg-aces' : 'bg-light-gray'"
          >
            <MailIcon
              :class="[
                otpFlow === 'verify-email' ? 'text-white' : 'text-dark-gray',
                'w-4 h-4',
              ]"
            />
          </div>
          <span
            :class="[
              otpFlow === 'verify-email' ? 'text-black font-medium' : 'text-dark-gray',
              'text-sm',
            ]"
          >Email</span>
        </div>
      </div>

      <div class="text-center mb-6">
        <div class="w-12 h-12 bg-aces bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-3">
          <DeviceMobileIcon
            v-if="otpFlow === 'verify-phone'"
            class="w-6 h-6 text-aces"
          />
          <MailIcon v-else class="w-6 h-6 text-aces" />
        </div>
        <h3 class="text-lg font-bold text-black">
          Verificá tu {{ otpFlow === 'verify-phone' ? 'teléfono' : 'email' }}
        </h3>
        <p class="text-sm text-dark-gray mt-2">
          {{ otpFlow === 'verify-phone' ? 'Te enviamos un código por SMS al ' : 'Te enviamos un código por email a ' }}
          <span class="font-medium text-black break-all">{{ destinationHint }}</span>
        </p>
        <p
          v-if="otpFlow === 'verify-phone' && registrationPhoneFormatInvalid"
          class="text-xs text-red-warning mt-2"
        >
          El teléfono no tiene un formato válido para SMS.
        </p>
        <p class="text-xs text-dark-gray mt-2">
          ¿No es correcto?
          <button
            type="button"
            class="text-aces hover:underline font-medium"
            @click="cancelToForm"
          >
            Volvé atrás y corregí mail o teléfono
          </button>
        </p>
      </div>

      <div class="mb-4">
        <label class="block text-sm text-dark-gray mb-3 text-center">Código</label>
        <Transition
          mode="out-in"
          enter-active-class="transition-all duration-200 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition-all duration-150 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="otpBusy"
            key="skeleton"
            aria-live="polite"
            aria-busy="true"
          >
            <div class="flex gap-2 justify-center">
              <div
                v-for="i in 6"
                :key="i"
                class="w-11 h-14 rounded-lg bg-light-gray animate-pulse"
                :style="{ animationDelay: `${i * 80}ms` }"
              />
            </div>
            <p class="text-sm text-dark-gray text-center mt-3 font-medium">
              {{ otpBusyMessage }}
            </p>
          </div>
          <div v-else key="otp">
            <OtpInput
              ref="otpInput"
              v-model="code"
              :length="6"
              :invalid="wrongCode"
              @complete="onOtpComplete"
            />
            <p v-if="wrongCode" class="text-red-warning text-sm text-center mt-3">
              El código no coincide con el enviado.
            </p>
          </div>
        </Transition>
      </div>

      <p
        v-if="!otpBusy"
        class="text-sm text-dark-gray mt-4 text-center"
      >
        <template v-if="resendSecondsLeft > 0">
          Si no te llegó, podés pedir otro en
          <span class="font-medium">{{ resendSecondsLeft }}s</span>.
        </template>
        <template v-else>
          <button
            type="button"
            :disabled="otpSending"
            class="text-aces hover:underline font-medium disabled:opacity-60"
            @click="resendCode"
          >
            {{ otpSending ? "Reenviando..." : "Reenviar código" }}
          </button>
        </template>
      </p>

      <p
        v-if="otpFlow === 'verify-email'"
        class="text-xs text-dark-gray mt-2 text-center"
      >
        Revisá la carpeta de spam si no lo encontrás en tu bandeja de entrada.
      </p>

      <button
        :disabled="otpBusy || code.length !== 6"
        class="btn-primary mt-6 lg:text-lg"
        @click="handleSubmit"
      >
        {{ otpVerifying ? "Verificando..." : otpSending ? "Enviando..." : "Confirmar" }}
      </button>

      <button
        v-if="otpFlow === 'verify-phone' || otpFlow === 'verify-email'"
        type="button"
        :disabled="otpBusy"
        class="w-full text-aces font-medium py-2 mt-2 hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
        @click="cancelToForm"
      >
        Volver y corregir datos
      </button>
    </div>

    <TransitionRoot appear :show="isOpen" as="template">
      <Dialog as="div" @close="closeExistingUserModal">
        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="min-h-screen px-4 text-center">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <DialogOverlay class="fixed inset-0 bg-black bg-opacity-75" />
            </TransitionChild>

            <span class="inline-block h-screen align-middle" aria-hidden="true">
              &#8203;
            </span>

            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <div
                class="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
              >
                <DialogTitle as="h3" class="text-lg font-medium leading-6 text-black">
                  <div class="flex items-center justify-between">
                    <h2 class="text-2xl font-medium text-black">Usuario existente</h2>
                    <button
                      type="button"
                      class="-mr-2 w-10 h-10 bg-white p-2 rounded-md flex items-center justify-center text-dark-gray"
                      @click="closeExistingUserModal"
                    >
                      <span class="sr-only">Cerrar</span>
                      <XIcon class="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </DialogTitle>
                <div class="mt-4">
                  <p class="text-sm text-dark-gray">
                    El {{ existingUser.docType }}
                    {{ existingUser.documentNumber }} ya está registrado
                    <template v-if="existingUser.firstName">
                      a nombre de {{ existingUser.firstName }} {{ existingUser.lastName }}.
                    </template>
                    <template v-else>en el sistema.</template>
                  </p>
                  <p class="mt-3 text-sm text-dark-gray">
                    Volvé al login con tu número de documento para entrar a tu
                    cuenta.
                  </p>
                  <button
                    type="button"
                    class="btn-primary my-4 lg:text-lg"
                    @click="goToLogin"
                  >
                    Ir al login
                  </button>
                  <i class="block px-1 text-xs text-dark-gray">
                    *Ante cualquier inconveniente, comunicate con tu coordinador.
                  </i>
                </div>
              </div>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script>
  import { ref, watch, inject, watchEffect } from "vue";
  import axios from "axios";
  import {
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay,
    DialogTitle,
  } from "@headlessui/vue";
  import {
    CheckIcon,
    SelectorIcon,
    XIcon,
    ExclamationCircleIcon,
    DeviceMobileIcon,
    MailIcon,
  } from "@heroicons/vue/solid";
  import { createToast } from "mosha-vue-toastify";
  import { setSubscriberToken } from "../auth";
  import OtpInput from "./OtpInput.vue";
  import {
    INVALID_FORMAT_MESSAGE,
    isValidArgentinePhone,
    normalizePhoneInput,
  } from "../utils/argentinePhone";

  export default {
    components: {
      Listbox,
      ListboxButton,
      ListboxOptions,
      ListboxOption,
      TransitionRoot,
      TransitionChild,
      Dialog,
      DialogOverlay,
      DialogTitle,
      CheckIcon,
      SelectorIcon,
      XIcon,
      ExclamationCircleIcon,
      DeviceMobileIcon,
      MailIcon,
      OtpInput,
    },
    setup() {
      const isLoading = ref(false);
      const person = ref({});
      const provinces = ref([]);
      const selectedProvince = ref({});
      const churches = ref([]);
      const selectedChurch = ref(null);
      const churchSearch = ref("");
      const docList = ["DNI", "LC", "LE"];
      const selectedDocType = ref(docList[0]);
      const isOpen = ref(false);
      const globalState = inject("globalState");

      const getprovinces = async () => {
        isLoading.value = true;
        await axios
          .get("Provinces/all")
          .then((res) => {
            provinces.value = res.data;
            selectedProvince.value = res.data[0];
          })
          .catch((err) => {
            console.error("Error al obtener provincias:", err);
          });
      };
      getprovinces();

      watch(
        () => globalState.tokenUpdated,
        (newVal) => {
          if (newVal === true) {
            getprovinces();
            globalState.tokenUpdated = false;
          }
        }
      );

      watch(
        () => selectedProvince.value?.provinceId,
        async (provinceId, oldId) => {
          if (!provinceId) {
            return;
          }
          const { data } = await axios.get(
            `Churches/byProvince?provinceId=${provinceId}`
          );
          churches.value = data;
          if (oldId && oldId !== provinceId) {
            selectedChurch.value = null;
            churchSearch.value = "";
            return;
          }
          const churchClientId =
            person.value.churchClientId ??
            selectedChurch.value?.churchClientId;
          if (churchClientId) {
            const match = data.find(
              (c) => c.churchClientId === churchClientId
            );
            if (match) {
              selectedChurch.value = match;
              churchSearch.value = match.churchName;
            }
          }
        }
      );

      watchEffect(() => {
        if (
          selectedChurch.value != null &&
          selectedChurch.value.churchClientId !== undefined
        ) {
          person.value.churchClientId = selectedChurch.value.churchClientId;
        }
      });
      watchEffect(() => {
        person.value.docType = selectedDocType.value;
      });

      return {
        person,
        provinces,
        selectedProvince,
        churches,
        selectedChurch,
        churchSearch,
        docList,
        selectedDocType,
        isOpen,
      };
    },
    data() {
      return {
        filteredList: [],
        searching: false,
        code: "",
        wrongCode: false,
        /** null | "verify-phone" | "verify-email" */
        otpFlow: null,
        otpSending: false,
        otpVerifying: false,
        /** Evita doble POST en alta (searchClient + Clients/create). */
        creating: false,
        resendSecondsLeft: 0,
        resendTimerId: null,
        createdClient: null,
        existingUser: {},
        maskedPhone: "",
        maskedEmail: "",
        phoneError: "",
        /** Paso OTP al que volver tras corregir contacto */
        returnToOtpFlow: null,
      };
    },
    computed: {
      formDisabled() {
        return this.creating || this.otpFlow !== null;
      },
      isEditingRegistration() {
        const c = this.createdClient;
        return !!(c && (c.clientId ?? c.ClientId) && this.otpFlow === null);
      },
      destinationHint() {
        if (this.otpFlow === "verify-phone") {
          const p = this.person?.phoneNumber;
          return p ? String(p).trim() : "tu teléfono";
        }
        const e = this.person?.email;
        return e ? String(e).trim() : "tu correo";
      },
      otpBusy() {
        return this.otpVerifying || this.otpSending;
      },
      submitDisabled() {
        return this.creating || this.otpSending || this.otpVerifying;
      },
      otpBusyMessage() {
        if (this.otpVerifying) return "Verificando código...";
        return "Enviando código...";
      },
      registrationContactPhone() {
        const p = this.person?.phoneNumber;
        return p ? String(p).trim() : "—";
      },
      registrationContactEmail() {
        const e = this.person?.email;
        return e ? String(e).trim() : "—";
      },
      registrationPhoneFormatInvalid() {
        const p = this.registrationContactPhone;
        return p !== "—" && !isValidArgentinePhone(p);
      },
      submitButtonLabel() {
        if (this.otpVerifying) return "Verificando...";
        if (this.otpFlow !== null) return "Verificar código";
        if (this.creating) {
          return this.isEditingRegistration
            ? "Guardando..."
            : "Creando usuario...";
        }
        if (this.otpSending) return "Enviando...";
        if (this.isEditingRegistration) return "Guardar y reenviar código";
        return "Crear Usuario";
      },
    },
    beforeUnmount() {
      this.clearResendCooldown();
    },
    watch: {
      selectedChurch(newChurch) {
        console.log("[NewUser] selectedChurch changed:", newChurch);
        if (newChurch?.churchName) {
          this.churchSearch = newChurch.churchName;
          this.searching = false;
        }
      },
      otpFlow(newVal) {
        if (newVal === null) {
          this.$nextTick(() => this.syncChurchSearchFromSelection());
        }
      },
    },
    methods: {
      syncChurchSearchFromSelection() {
        if (this.selectedChurch?.churchName) {
          this.churchSearch = this.selectedChurch.churchName;
          return;
        }
        const churchClientId =
          this.person?.churchClientId ??
          this.createdClient?.churchClientId ??
          this.createdClient?.ChurchClientID;
        if (!churchClientId || !this.churches?.length) {
          return;
        }
        const match = this.churches.find(
          (c) => c.churchClientId === churchClientId
        );
        if (match) {
          this.selectedChurch = match;
          this.churchSearch = match.churchName;
        }
      },
      maskEmail(email) {
        if (!email || !email.includes("@")) return email;
        const [local, domain] = email.split("@");
        const visible = Math.min(3, local.length);
        return `${local.slice(0, visible)}***@${domain}`;
      },
      filterChurchList(fas) {
        if (fas !== "") this.searching = true;
        this.filteredList = this.churches.filter((x) =>
          x.churchName.toLowerCase().includes(fas.toLowerCase())
        );
      },
      selectChurchOnClick(church) {
        this.searching = false;
        if (church?.churchName) {
          this.churchSearch = church.churchName;
        }
      },
      validateData() {
        if (typeof this.person.phoneNumber === "string") {
          this.person.phoneNumber = normalizePhoneInput(this.person.phoneNumber);
        }
        if (typeof this.person.documentNumber === "string") {
          this.person.documentNumber = this.person.documentNumber
            .replace(/\s/g, "")
            .replace(/-/g, "")
            .replace(/\./g, "");
        }
      },
      onPhoneInput() {
        this.validateData();
        if (!this.person.phoneNumber) {
          this.phoneError = "";
          return;
        }
        this.phoneError = isValidArgentinePhone(this.person.phoneNumber)
          ? ""
          : INVALID_FORMAT_MESSAGE;
      },
      validatePhoneBeforeSubmit() {
        this.onPhoneInput();
        if (!isValidArgentinePhone(this.person.phoneNumber)) {
          this.phoneError = INVALID_FORMAT_MESSAGE;
          createToast(INVALID_FORMAT_MESSAGE, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
          return false;
        }
        this.person.phoneNumber = normalizePhoneInput(this.person.phoneNumber);
        return true;
      },
      validateChurchSelection() {
        if (!this.selectedChurch) {
          this.churchSearch = "";
          document.getElementById("searchBox")?.focus();
          return false;
        }
        if (this.selectedChurch.churchName.trim() !== String(this.churchSearch ?? "").trim()) {
          this.churchSearch = "";
          this.selectedChurch = null;
          document.getElementById("searchBox")?.focus();
          return false;
        }
        return true;
      },
      async handleSubmit() {
        if (this.otpFlow === "verify-phone") {
          await this.verifyPhoneCode();
          return;
        }
        if (this.otpFlow === "verify-email") {
          await this.verifyEmailCode();
          return;
        }
        if (this.creating) {
          return;
        }
        console.log("[NewUser] handleSubmit person:", JSON.parse(JSON.stringify(this.person)));
        console.log("[NewUser] handleSubmit selectedChurch:", this.selectedChurch);
        console.log("[NewUser] handleSubmit churchSearch:", this.churchSearch);
        if (!this.validateChurchSelection()) {
          return;
        }
        if (!this.validatePhoneBeforeSubmit()) {
          return;
        }
        await this.startCreation();
      },
      onOtpComplete(code) {
        if (!this.otpBusy) {
          const trimmed = String(code ?? this.code ?? "").trim();
          if (trimmed.length === 6) {
            this.code = trimmed;
          }
          this.handleSubmit();
        }
      },
      cancelToForm() {
        this.returnToOtpFlow = this.otpFlow;
        this.otpFlow = null;
        this.code = "";
        this.wrongCode = false;
        this.maskedPhone = "";
        this.maskedEmail = "";
        this.clearResendCooldown();
        this.$nextTick(() => this.syncChurchSearchFromSelection());
      },
      clientIdOf(client) {
        if (!client) return null;
        return client.clientId ?? client.ClientId ?? null;
      },
      async updateRegistrationContactAndResend() {
        const clientId = this.clientIdOf(this.createdClient);
        if (!clientId) {
          return false;
        }
        const docType = this.person.docType || this.selectedDocType;
        const documentNumber = String(this.person.documentNumber ?? "").trim();
        this.otpSending = true;
        try {
          const { data } = await axios.patch(
            "subscriber-auth/registration/contact",
            {
              clientId,
              docType,
              documentNumber,
              email: this.person.email,
              phoneNumber: this.person.phoneNumber,
            }
          );
          if (!data.success) {
            createToast(data.errorMessage || "No se pudieron actualizar los datos.", {
              position: "top-center",
              type: "danger",
              transition: "zoom",
            });
            return false;
          }
          this.createdClient = {
            ...(this.createdClient || {}),
            clientId: data.clientId ?? clientId,
            ClientId: data.clientId ?? clientId,
            email: this.person.email,
            phoneNumber: this.person.phoneNumber,
            phoneVerified: data.phoneVerified,
            emailVerified: data.emailVerified,
            PhoneVerified: data.phoneVerified,
            EmailVerified: data.emailVerified,
          };
          const targetFlow =
            this.returnToOtpFlow ||
            (data.phoneVerified ? "verify-email" : "verify-phone");
          this.returnToOtpFlow = null;
          this.code = "";
          this.wrongCode = false;
          this.maskedPhone = data.maskedPhone || "";
          this.maskedEmail = data.maskedEmail || "";

          const sent =
            targetFlow === "verify-email"
              ? await this.sendEmailVerification()
              : await this.sendPhoneVerification();
          if (sent) {
            this.startResendCooldown();
            createToast(
              targetFlow === "verify-email"
                ? "Datos actualizados. Te enviamos un código por email."
                : "Datos actualizados. Te enviamos un código por SMS.",
              {
                position: "top-center",
                type: "success",
                transition: "zoom",
              }
            );
          }
          return sent;
        } catch (err) {
          const body = err.response?.data;
          const msg =
            (typeof body === "string" && body) ||
            body?.errorMessage ||
            body?.title ||
            "No se pudieron actualizar los datos.";
          createToast(msg, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
          return false;
        } finally {
          this.otpSending = false;
        }
      },
      async startCreation() {
        if (this.creating) {
          return;
        }

        const dni = String(this.person.documentNumber ?? "").trim();
        const docType = this.person.docType || this.selectedDocType;

        this.creating = true;
        try {
          if (this.clientIdOf(this.createdClient)) {
            await this.updateRegistrationContactAndResend();
            return;
          }

          const existing = await this.lookupExistingUser(dni, docType);
          if (existing) {
            this.existingUser = existing;
            this.isOpen = true;
            return;
          }
          const created = await this.createClient();
          if (!created) {
            return;
          }
          this.createdClient = created;
          await this.sendPhoneVerification();
        } finally {
          this.creating = false;
        }
      },
      async lookupExistingUser(dni, docType) {
        try {
          const { data, status } = await axios.get(
            `Clients/searchClient?dni=${dni}&docType=${docType}`,
            { validateStatus: (s) => s === 200 || s === 404 }
          );
          if (status === 200 && data && (data.clientId || data.ClientId)) {
            return data;
          }
          return null;
        } catch (err) {
          console.warn("[NewUser] searchClient:", err?.message || err);
          return null;
        }
      },
      async createClient() {
        try {
          const { data, status } = await axios.post(
            "Clients/create",
            this.person,
            { validateStatus: (s) => s < 500 }
          );
          if (status === 201) {
            return data;
          }
          if (status === 406) {
            createToast(
              `Ya existe un usuario con el documento ${this.person.docType} ${this.person.documentNumber}.`,
              { position: "top-center", type: "danger", transition: "zoom" }
            );
            return null;
          }
          const msg =
            (typeof data === "string" && data) ||
            data?.errorMessage ||
            data?.title ||
            "No se pudo crear el usuario.";
          createToast(msg, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
          return null;
        } catch (err) {
          const body = err.response?.data;
          const msg =
            (typeof body === "string" && body) ||
            body?.errorMessage ||
            body?.title ||
            "No se pudo crear el usuario.";
          createToast(msg, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
          return null;
        }
      },
      async sendPhoneVerification() {
        const c = this.createdClient || {};
        const clientId = c.clientId ?? c.ClientId;
        if (!clientId) {
          createToast("No se pudo iniciar la verificación. Volvé a intentar.", {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
          return false;
        }
        this.otpSending = true;
        try {
          const { data } = await axios.post(
            "subscriber-auth/phone/send-verification-code",
            {
              clientId,
              docType: this.person.docType || this.selectedDocType,
              documentNumber: String(this.person.documentNumber ?? "").trim(),
            }
          );
          if (data.success) {
            this.otpFlow = "verify-phone";
            this.code = "";
            this.wrongCode = false;
            this.maskedPhone = data.maskedPhone || "";
            this.startResendCooldown();
            createToast("Te enviamos un código por SMS.", {
              position: "top-center",
              type: "success",
              transition: "zoom",
            });
            return true;
          }
          createToast(data.errorMessage || "No se pudo enviar el código.", {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
          return false;
        } catch (err) {
          const body = err.response?.data;
          const msg =
            (typeof body === "string" && body) ||
            body?.errorMessage ||
            body?.title ||
            "No se pudo enviar el código.";
          createToast(msg, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
          return false;
        } finally {
          this.otpSending = false;
        }
      },
      async sendEmailVerification() {
        const c = this.createdClient || {};
        const clientId = c.clientId ?? c.ClientId;
        if (!clientId) return false;
        this.otpSending = true;
        try {
          const { data } = await axios.post(
            "subscriber-auth/email/send-verification-code",
            {
              clientId,
              docType: this.person.docType || this.selectedDocType,
              documentNumber: String(this.person.documentNumber ?? "").trim(),
            }
          );
          if (data.success) {
            this.otpFlow = "verify-email";
            this.code = "";
            this.wrongCode = false;
            this.maskedEmail = data.maskedEmail || "";
            this.startResendCooldown();
            createToast("Te enviamos un código por email.", {
              position: "top-center",
              type: "success",
              transition: "zoom",
            });
            return true;
          }
          createToast(data.errorMessage || "No se pudo enviar el código por email.", {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
          return false;
        } catch (err) {
          const body = err.response?.data;
          const msg =
            (typeof body === "string" && body) ||
            body?.errorMessage ||
            body?.title ||
            "No se pudo enviar el código por email.";
          createToast(msg, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
          return false;
        } finally {
          this.otpSending = false;
        }
      },
      async verifyPhoneCode() {
        const c = this.createdClient || {};
        const clientId = c.clientId ?? c.ClientId;
        const trimmed = String(this.code ?? "").trim();
        if (!clientId) {
          createToast("Sesión incompleta. Volvé a empezar.", {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
          return;
        }
        if (!trimmed) {
          createToast("Ingresá el código.", {
            position: "top-center",
            type: "warning",
            transition: "zoom",
          });
          return;
        }
        this.otpVerifying = true;
        try {
          const { data } = await axios.post(
            "subscriber-auth/phone/verify-code",
            {
              clientId,
              docType: this.person.docType || this.selectedDocType,
              documentNumber: String(this.person.documentNumber ?? "").trim(),
              code: trimmed,
            }
          );
          if (data.success && data.token) {
            this.wrongCode = false;
            this.code = "";
            await setSubscriberToken(data.token);
            // Continúa al paso de email sin emitir update todavía. No limpio el cooldown acá:
            // sendEmailVerification → startResendCooldown lo reinicia atómicamente, así
            // evitamos un instante donde el bloque de "Reenviar" aparezca durante la transición.
            await this.sendEmailVerification();
            return;
          }
          if (data.success && !data.token) {
            createToast(
              "Verificado, pero el servidor no devolvió la sesión. Volvé al login para iniciar sesión.",
              { position: "top-center", type: "warning", transition: "zoom" }
            );
            this.$emit("changeStep", 0);
            return;
          }
          this.wrongCode = true;
          if (data.errorMessage) {
            createToast(data.errorMessage, {
              position: "top-center",
              type: "danger",
              transition: "zoom",
            });
          }
        } catch (err) {
          this.wrongCode = true;
          const body = err.response?.data;
          const msg =
            body?.errorMessage ||
            body?.title ||
            "Código incorrecto o expirado.";
          createToast(msg, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
        } finally {
          this.otpVerifying = false;
        }
      },
      async verifyEmailCode() {
        const c = this.createdClient || {};
        const clientId = c.clientId ?? c.ClientId;
        const trimmed = String(this.code ?? "").trim();
        if (!clientId) return;
        if (!trimmed) {
          createToast("Ingresá el código.", {
            position: "top-center",
            type: "warning",
            transition: "zoom",
          });
          return;
        }
        this.otpVerifying = true;
        try {
          const { data } = await axios.post(
            "subscriber-auth/email/verify-code",
            {
              clientId,
              docType: this.person.docType || this.selectedDocType,
              documentNumber: String(this.person.documentNumber ?? "").trim(),
              code: trimmed,
            }
          );
          if (data.success) {
            this.wrongCode = false;
            this.code = "";
            this.clearResendCooldown();
            if (data.token) {
              await setSubscriberToken(data.token);
            }
            // Refleja en el cliente local los flags verificados
            const updated = {
              ...(this.createdClient || {}),
              emailVerified: true,
              phoneVerified: true,
            };
            this.$emit("update", updated);
            return;
          }
          this.wrongCode = true;
          if (data.errorMessage) {
            createToast(data.errorMessage, {
              position: "top-center",
              type: "danger",
              transition: "zoom",
            });
          }
        } catch (err) {
          this.wrongCode = true;
          const body = err.response?.data;
          const msg =
            body?.errorMessage ||
            body?.title ||
            "Código incorrecto o expirado.";
          createToast(msg, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
        } finally {
          this.otpVerifying = false;
        }
      },
      async resendCode() {
        if (this.resendSecondsLeft > 0 || this.otpSending) return;
        const ok =
          this.otpFlow === "verify-phone"
            ? await this.sendPhoneVerification()
            : await this.sendEmailVerification();
        if (ok) {
          createToast("Código reenviado.", {
            position: "top-center",
            type: "success",
            transition: "zoom",
          });
        }
      },
      startResendCooldown(seconds = 30) {
        this.clearResendCooldown();
        this.resendSecondsLeft = seconds;
        this.resendTimerId = setInterval(() => {
          this.resendSecondsLeft -= 1;
          if (this.resendSecondsLeft <= 0) {
            this.clearResendCooldown();
          }
        }, 1000);
      },
      clearResendCooldown() {
        if (this.resendTimerId) {
          clearInterval(this.resendTimerId);
        }
        this.resendTimerId = null;
        this.resendSecondsLeft = 0;
      },
      closeExistingUserModal() {
        this.isOpen = false;
      },
      goToLogin() {
        this.isOpen = false;
        this.$emit("changeStep", 0);
      },
    },
  };
</script>
